import React, { useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { AdminNotificationBell } from './notifications'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Shield,
  Users,
  FileText,
  Layout,
  BarChart3,
  Settings,
  Menu,
  X,
  Home,
  CreditCard,
  Activity,
  Database,
  Bell,
  Sparkles,
  Sun,
  Moon,
  Globe,
  Monitor,
  School,
  Bot,
  MessageSquare,
  Package,
  ChevronDown,
  ChevronRight,
  Clock,
  Layers,
  Gift,
  TrendingUp,
  ShieldCheck,
  BookOpen,
  PenTool,
  FolderTree,
  Tag,
  Mail,
  Calendar,
  Handshake,
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'

interface AdminSidebarSubItem {
  id: string
  label: string
  path: string
  badge?: number
}

interface AdminSidebarItem {
  id: string
  label: string
  icon: React.ComponentType<any>
  path: string
  badge?: number
  subItems?: AdminSidebarSubItem[]
}

interface AdminSidebarCategory {
  id: string
  label: string
  icon: React.ComponentType<any>
  items: AdminSidebarItem[]
  collapsible?: boolean
}

const adminSidebarCategories: AdminSidebarCategory[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: BarChart3,
    items: [
      { id: 'overview', label: 'Visão Geral', icon: BarChart3, path: '/admin' },
      { id: 'analytics', label: 'Analytics', icon: Activity, path: '/admin/analytics' }
    ]
  },
  {
    id: 'content',
    label: 'Conteúdo',
    icon: FileText,
    items: [
      { id: 'questions', label: 'Questões', icon: FileText, path: '/admin/questions' },
      { id: 'bulk-generator', label: 'Gerador em Massa', icon: Sparkles, path: '/admin/bulk-generator' },
      { id: 'bulk-review', label: 'Revisar Questões IA', icon: Bot, path: '/admin/bulk-review' },
      { id: 'bulk-jobs', label: 'Jobs de Geração (sync)', icon: Activity, path: '/admin/bulk-jobs' },
      { id: 'generation-batches', label: 'Lotes (Batches)', icon: Layers, path: '/admin/generation-batches' },
      { id: 'auto-generation', label: 'Geração Recorrente', icon: Clock, path: '/admin/auto-generation' },
      { id: 'assessments', label: 'Avaliações', icon: Database, path: '/admin/assessments' },
      { id: 'public-assessments', label: 'Avaliações Públicas', icon: Globe, path: '/admin/public-assessments' },
      { id: 'templates', label: 'Modelos Prontos', icon: Layout, path: '/admin/templates' },
      { id: 'feedback', label: 'Feedbacks', icon: MessageSquare, path: '/admin/feedback' }
    ]
  },
  {
    id: 'users',
    label: 'Usuários & Escolas',
    icon: Users,
    items: [
      { id: 'users', label: 'Usuários', icon: Users, path: '/admin/users' },
      { id: 'schools', label: 'Escolas', icon: School, path: '/admin/schools' },
      { id: 'notifications', label: 'Notificações', icon: Bell, path: '/admin/notifications' }
    ]
  },
  {
    id: 'billing',
    label: 'Financeiro',
    icon: CreditCard,
    items: [
      { id: 'plans', label: 'Planos', icon: Package, path: '/admin/plans' },
      { id: 'subscriptions', label: 'Assinaturas', icon: CreditCard, path: '/admin/subscriptions' },
      { id: 'trials', label: 'Trials', icon: Clock, path: '/admin/trials' }
    ]
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: TrendingUp,
    items: [
      { id: 'campanhas', label: 'Campanhas Sazonais', icon: Gift, path: '/admin/campanhas' },
      { id: 'campanhas-leads', label: 'Leads', icon: Users, path: '/admin/campanhas/leads' },
      { id: 'planos-personalizados', label: 'Planos personalizados', icon: Handshake, path: '/admin/planos-personalizados' }
    ]
  },
  {
    id: 'blog',
    label: 'Blog',
    icon: BookOpen,
    items: [
      { id: 'blog-posts', label: 'Posts', icon: FileText, path: '/admin/blog' },
      { id: 'blog-new', label: 'Novo Post', icon: PenTool, path: '/admin/blog/novo' },
      { id: 'blog-auto-gen', label: 'Geração Automática', icon: Bot, path: '/admin/blog/auto-generation' },
      { id: 'blog-calendar', label: 'Calendário', icon: Calendar, path: '/admin/blog/calendario' },
      { id: 'blog-categories', label: 'Categorias', icon: FolderTree, path: '/admin/blog/categorias' },
      { id: 'blog-tags', label: 'Tags', icon: Tag, path: '/admin/blog/tags' },
      { id: 'blog-newsletter', label: 'Newsletter', icon: Mail, path: '/admin/blog/newsletter' },
    ]
  },
  {
    id: 'system',
    label: 'Sistema',
    icon: Settings,
    items: [
      { id: 'security', label: 'Dashboard de Segurança', icon: ShieldCheck, path: '/admin/security' },
      { id: 'seo', label: 'SEO & Sitemap', icon: Globe, path: '/admin/seo' },
      { id: 'ai-providers', label: 'Provedores de IA', icon: Bot, path: '/admin/ai-providers' },
      { id: 'settings', label: 'Configurações', icon: Settings, path: '/admin/settings' }
    ]
  }
]

const AdminLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>(() => {
    return adminSidebarCategories.reduce((acc, category) => {
      const hasActiveItem = category.items.some(item => {
        if (item.path === '/admin' || item.path === '/admin/blog') {
          return location.pathname === item.path
        }
        if (item.subItems) {
          return item.subItems.some(sub => location.pathname.startsWith(sub.path)) || location.pathname.startsWith(item.path)
        }
        return location.pathname.startsWith(item.path)
      })
      acc[category.id] = hasActiveItem
      return acc
    }, {} as Record<string, boolean>)
  })
  const { profile, signOut, isAdmin, isEditor } = useAuth()
  const { theme, setTheme } = useTheme()

  // Filter sidebar: editors only see the Blog section
  const visibleCategories = (!isAdmin && isEditor)
    ? adminSidebarCategories.filter(c => c.id === 'blog')
    : adminSidebarCategories

  const isActive = (path: string) => {
    if (path === '/admin' || path === '/admin/blog' || path === '/admin/campanhas') {
      return location.pathname === path
    }
    return location.pathname.startsWith(path)
  }

  const isItemActive = (item: AdminSidebarItem) => {
    if (item.subItems) {
      return item.subItems.some(subItem => isActive(subItem.path)) || isActive(item.path)
    }
    return isActive(item.path)
  }

  const isCategoryActive = (category: AdminSidebarCategory) => {
    return category.items.some(item => isItemActive(item))
  }

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }))
  }

  const handleNavigation = (path: string) => {
    navigate(path)
    setSidebarOpen(false)
  }

  const handleBackToApp = () => {
    navigate('/app')
  }

  // Get effective theme (resolve 'system' to actual theme)
  const getEffectiveTheme = () => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return theme
  }

  const toggleTheme = () => {
    const effectiveTheme = getEffectiveTheme()
    if (effectiveTheme === 'light') {
      setTheme('dark')
    } else {
      setTheme('light')
    }
  }

  const getThemeIcon = () => {
    const effectiveTheme = getEffectiveTheme()
    if (effectiveTheme === 'light') return <Sun className="w-4 h-4" />
    return <Moon className="w-4 h-4" />
  }

  const getThemeText = () => {
    const effectiveTheme = getEffectiveTheme()
    if (effectiveTheme === 'light') return 'Tema Claro'
    return 'Tema Escuro'
  }

  return (
    <div className="flex h-screen bg-cream-100 dark:bg-ink-900 overflow-hidden">
      {/* Mobile Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside className={`
        fixed lg:relative inset-y-0 left-0 z-50 w-64 bg-surface dark:bg-ink-700 border-r border-cream-400 dark:border-ink-500 flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Header */}
        <div className="p-6 border-b border-cream-400 dark:border-ink-500">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-terra-500 rounded-caderno-md flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold font-display text-ink-900 dark:text-cream-50">Admin Panel</h1>
                <p className="text-xs font-mono text-ink-400 dark:text-ink-300">Atividade Pronta</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 hover:bg-cream-100 dark:hover:bg-ink-700/60 rounded-caderno-sm lg:hidden"
            >
              <X className="w-5 h-5 text-ink-400 dark:text-ink-300" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {visibleCategories.map((category) => {
            const CategoryIcon = category.icon
            const isExpanded = expandedCategories[category.id]
            const categoryActive = isCategoryActive(category)

            return (
              <div key={category.id} className="space-y-1">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category.id)}
                  className={`w-full flex items-center space-x-2 px-3 py-2 rounded-caderno-sm transition-all duration-200 text-left group ${
                    categoryActive
                      ? 'bg-terra-100 dark:bg-terra-500/20 text-terra-700 dark:text-terra-300'
                      : 'text-ink-500 dark:text-cream-300 hover:bg-cream-100 dark:hover:bg-ink-700/50'
                  }`}
                >
                  <CategoryIcon className={`w-4 h-4 ${
                    categoryActive
                      ? 'text-terra-700 dark:text-terra-300'
                      : 'text-ink-400 dark:text-cream-300'
                  }`} />
                  <span className="flex-1 text-sm font-semibold uppercase tracking-wide">
                    {category.label}
                  </span>
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>

                {/* Category Items */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-1 ml-2"
                    >
                      {category.items.map((item) => {
                        const ItemIcon = item.icon
                        const itemActive = isItemActive(item)

                        return (
                          <div key={item.id}>
                            {/* Main Item */}
                            <button
                              onClick={() => handleNavigation(item.path)}
                              className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-caderno-sm transition-all duration-200 text-left group ${
                                itemActive
                                  ? 'bg-terra-100 dark:bg-terra-500/20 text-terra-700 dark:text-terra-300 border border-terra-100 dark:border-terra-700'
                                  : 'text-ink-700 dark:text-cream-300 hover:bg-cream-100 dark:hover:bg-ink-700/50'
                              }`}
                            >
                              <ItemIcon className={`w-4 h-4 ${
                                itemActive
                                  ? 'text-terra-700 dark:text-terra-300'
                                  : 'text-ink-400 dark:text-ink-300 group-hover:text-ink-700 dark:group-hover:text-cream-300'
                              }`} />
                              <span className="flex-1 font-medium text-sm">{item.label}</span>

                              {item.badge && (
                                <span className="bg-terra-100 dark:bg-terra-500/20 text-terra-700 dark:text-terra-300 text-xs px-2 py-0.5 rounded-full">
                                  {item.badge}
                                </span>
                              )}
                            </button>

                            {/* Sub Items */}
                            {item.subItems && itemActive && (
                              <div className="ml-6 mt-1 space-y-1">
                                {item.subItems.map((subItem) => {
                                  const subItemActive = isActive(subItem.path)

                                  return (
                                    <button
                                      key={subItem.id}
                                      onClick={() => handleNavigation(subItem.path)}
                                      className={`w-full flex items-center space-x-2 px-3 py-1.5 rounded-caderno-sm transition-all duration-200 text-left ${
                                        subItemActive
                                          ? 'bg-terra-100 dark:bg-terra-500/20 text-terra-700 dark:text-terra-300'
                                          : 'text-ink-500 dark:text-ink-300 hover:bg-cream-100 dark:hover:bg-ink-700/30'
                                      }`}
                                    >
                                      <div className={`w-1.5 h-1.5 rounded-full ${
                                        subItemActive
                                          ? 'bg-terra-500 dark:bg-terra-300'
                                          : 'bg-cream-400 dark:bg-ink-500'
                                      }`} />
                                      <span className="text-xs font-medium">{subItem.label}</span>

                                      {subItem.badge && (
                                        <span className="bg-terra-100 dark:bg-terra-500/20 text-terra-700 dark:text-terra-300 text-xs px-1.5 py-0.5 rounded-full">
                                          {subItem.badge}
                                        </span>
                                      )}
                                    </button>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-cream-400 dark:border-ink-500">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-8 bg-cream-300 dark:bg-ink-500 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-ink-500 dark:text-cream-200">
                {profile?.nome?.charAt(0) || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-ink-900 dark:text-cream-50 truncate">
                {profile?.nome || 'Admin'}
              </p>
              <p className="text-xs text-ink-400 dark:text-ink-300">Administrador</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <button
              onClick={toggleTheme}
              className="w-full flex items-center justify-between text-ink-500 dark:text-cream-300 hover:text-ink-900 dark:hover:text-cream-200 px-3 py-2 rounded-caderno-sm hover:bg-cream-100 dark:hover:bg-ink-700/60 transition-colors text-sm"
            >
              <div className="flex items-center space-x-2">
                {getThemeIcon()}
                <span>Alternar tema</span>
              </div>
            </button>
            
            <button
              onClick={handleBackToApp}
              className="w-full flex items-center space-x-2 text-ink-500 dark:text-cream-300 hover:text-ink-900 dark:hover:text-cream-200 px-3 py-2 rounded-caderno-sm hover:bg-cream-100 dark:hover:bg-ink-700/60 transition-colors text-sm"
            >
              <Home className="w-4 h-4" />
              <span>Voltar ao App</span>
            </button>
            
            <button
              onClick={signOut}
              className="w-full flex items-center space-x-2 text-blood-500 hover:text-blood-700 dark:text-blood-300 dark:hover:text-blood-100 px-3 py-2 rounded-caderno-sm hover:bg-blood-100 dark:hover:bg-blood-700/20 transition-colors text-sm"
            >
              <span>Sair</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <header className="h-16 bg-surface dark:bg-ink-700 border-b border-cream-400 dark:border-ink-500 flex items-center w-full">
          <div className="flex items-center w-full px-4 lg:px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-cream-100 dark:hover:bg-ink-700/60 rounded-caderno-sm transition-colors lg:hidden mr-4"
            >
              <Menu className="w-5 h-5 text-ink-500 dark:text-ink-300" />
            </button>
            
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-ink-900 dark:text-cream-50">
                Painel de Administração
              </h2>
            </div>
            <AdminNotificationBell />
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto bg-cream-100 dark:bg-ink-900">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default AdminLayout